import { LinkResolver, LinkResponse, GS1LinkResolver, GS1LinkResponse, privateAPI } from 'react-component-utils';

/**
 * Generates a link resolver URL based on the provided LinkResolver and LinkResponse objects.
 *
 * @param linkResolver - An object containing information for link resolution.
 * @param linkResponses - An array of objects representing link responses.
 *
 * @returns The link resolver URL.
 *
 * @example
 * const linkResolver: LinkResolver = {
 *   identificationKeyType: 'nlisid',
 *   identificationKey: '3ABCD123XBDC0447',
 *   itemDescription: 'Deforestation-free Braford beef cattle',
 * };
 *
 * const linkResponses: LinkResponse[] = [
 *   {
 *     linkType: 'gs1:verificationService',
 *     linkTitle: 'VCKit verify service1',
 *     targetUrl: 'https://verify.com/dev/verifyCredential',
 *     mimeType: 'text/plain',
 *   },
 *   {
 *     linkType: 'gs1:certificationInfo',
 *     linkTitle: 'Livestock passport',
 *     targetUrl: 'https://storage.com/dlp-vc-did-web.json',
 *     mimeType: 'application/json',
 *   },
 * ];
 *
 * const resolverUrl = await createLinkResolver(linkResolver, linkResponses);
 * // Returns: http://localhost/nlisid/3ABCD123XBDC0447?linkType=all
 */
export const createLinkResolver = async (
    linkResolver: LinkResolver,
    linkResponses: LinkResponse[],
    responseLinkType: string = 'all',
    qualifierPath: string = '/',
    queryString: string | null = null,
): Promise<string> => {
    const registerQualifierPath = queryString ? qualifierPath + '?' + queryString : qualifierPath;
    const params: GS1LinkResolver[] = [constructLinkResolver(linkResolver, linkResponses, registerQualifierPath)];
    try {
        privateAPI.setBearerTokenAuthorizationHeaders(process.env.REACT_APP_DLR_API_KEY || '');
        await privateAPI.post<unknown>(`${process.env.REACT_APP_DLR_API_URL}/resolver`, params);
        return `${process.env.REACT_APP_DLR_API_URL}/${linkResolver.identificationKeyType}/${linkResolver.identificationKey}${qualifierPath}?linkType=${responseLinkType}`;
    } catch (error) {
        console.error(error);
        throw new Error('Error creating link resolver');
    }
};

const constructLinkResolver = (
    linkResolver: LinkResolver,
    linkResponses: LinkResponse[],
    qualifierPath: string = '/',
): GS1LinkResolver => {
    const gs1LinkResolver: GS1LinkResolver = {
        identificationKeyType: linkResolver.identificationKeyType,
        identificationKey: linkResolver.identificationKey,
        itemDescription: linkResolver.itemDescription,
        qualifierPath: qualifierPath,
        active: true,
        responses: [],
    };

    linkResponses.forEach((linkResponse: LinkResponse) => {
        const gs1LinkResponseForUS: GS1LinkResponse = {
            ianaLanguage: 'en',
            context: 'us',
            defaultLinkType: false,
            defaultIanaLanguage: false,
            defaultContext: false,
            defaultMimeType: false,
            fwqs: false,
            active: true,
            ...linkResponse,
        };

        const gs1LinkResponseForAU: GS1LinkResponse = {
            ianaLanguage: 'en',
            context: 'au',
            defaultLinkType: false,
            defaultIanaLanguage: false,
            defaultContext: false,
            defaultMimeType: false,
            fwqs: false,
            active: true,
            ...linkResponse,
        };

        gs1LinkResolver.responses.push(gs1LinkResponseForUS, gs1LinkResponseForAU);
    });

    return gs1LinkResolver;
};
